import { StatusModal } from '@/components/StatusModal';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { hasModule } from '@/lib/modules';
import type { MeResponse } from '@sklad-sync/shared';
import { useCallback, useEffect, useState } from 'react';

type Stav = 'nova' | 'objednano' | 'vyrizeno';

type Pozadavek = {
  id: number;
  text: string;
  jmeno: string;
  stav: Stav;
  created_at: number;
  created_by: string | null;
  updated_at: number | null;
  updated_by: string | null;
  /** 'automat' = založil sklad zipů, když zip chybí (viz Nákup). */
  zdroj?: string;
};

// V UI jen dva stavy (rozhodnutí 30. 9. 2026) — „vyrizeno" (starší záznamy,
// automaticky uzavřené) se ukazuje jako Objednáno.
const STAV_LABEL: Record<Stav, string> = {
  nova: 'Čeká',
  objednano: '✓ Objednáno',
  vyrizeno: '✓ Objednáno',
};
const STAVY_VYBER: Stav[] = ['nova', 'objednano'];

const STAV_CLASS: Record<Stav, string> = {
  nova: 'text-warning bg-warning-light',
  objednano: 'text-success bg-success-light',
  vyrizeno: 'text-success bg-success-light',
};

/** Jméno si pamatujeme v prohlížeči — na sdíleném tabletu ho nikdo nepřepisuje. */
const NAME_KEY = 'vyroba-objednavka-jmeno';

/** Unix ms → „4.8.2026 12:53". */
function fmtDateTime(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/**
 * Nová objednávka — nástěnka požadavků dílny. Kdokoliv sem napíše, co je
 * potřeba koupit (páska do tiskárny, káva, štítky), a kdo nákup vyřizuje,
 * posouvá stav.
 */
export function NovaObjednavkaPage({ user }: { user: MeResponse }) {
  // Vyřizovat objednávky (měnit stav, mazat) smí správce a kdo má právo na nákup.
  const canManage =
    hasModule(user.modules, 'user_admin') || hasModule(user.modules, 'vyroba_nakup');
  const [items, setItems] = useState<Pozadavek[] | null>(null);
  const [text, setText] = useState('');
  const [jmeno, setJmeno] = useState(() => localStorage.getItem(NAME_KEY) ?? '');
  const [busy, setBusy] = useState(false);
  const [hotove, setHotove] = useState(false);
  const [toast, setToast] = useState<{ msg: string; kind: 'success' | 'error' } | null>(null);

  const load = useCallback(async () => {
    try {
      const d = await apiFetch<{ items: Pozadavek[] }>('/api/vyroba/pozadavky');
      setItems(d.items);
    } catch (e: unknown) {
      setItems([]);
      setToast({
        msg: e instanceof ApiError ? e.describe() : 'Načtení seznamu selhalo',
        kind: 'error',
      });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function odeslat() {
    if (!text.trim() || !jmeno.trim()) {
      setToast({ msg: 'Vyplň text objednávky i jméno.', kind: 'error' });
      return;
    }
    setBusy(true);
    try {
      const r = await apiFetch<{ mail_error: string | null }>('/api/vyroba/pozadavky', {
        method: 'POST',
        body: { text: text.trim(), jmeno: jmeno.trim() },
      });
      localStorage.setItem(NAME_KEY, jmeno.trim());
      setText('');
      // Uloženo je to tak jako tak — o selhání mailu ale musí být vidět.
      setToast(
        r.mail_error
          ? { msg: `Objednávka uložena, ale e-mail se neodeslal: ${r.mail_error}`, kind: 'error' }
          : { msg: 'Objednávka odeslána', kind: 'success' },
      );
      await load();
    } catch (e: unknown) {
      setToast({ msg: e instanceof ApiError ? e.describe() : 'Odeslání selhalo', kind: 'error' });
    } finally {
      setBusy(false);
    }
  }

  async function zmenitStav(p: Pozadavek, stav: Stav) {
    // Optimisticky — ať tabulka neposkakuje při každém překliknutí.
    setItems((prev) => (prev ?? []).map((x) => (x.id === p.id ? { ...x, stav } : x)));
    try {
      await apiFetch(`/api/vyroba/pozadavky/${p.id}`, { method: 'PATCH', body: { stav } });
    } catch (e: unknown) {
      setToast({
        msg: e instanceof ApiError ? e.describe() : 'Změna stavu selhala',
        kind: 'error',
      });
      await load();
    }
  }

  async function smazat(p: Pozadavek) {
    setItems((prev) => (prev ?? []).filter((x) => x.id !== p.id));
    try {
      await apiFetch(`/api/vyroba/pozadavky/${p.id}`, { method: 'DELETE' });
    } catch (e: unknown) {
      setToast({ msg: e instanceof ApiError ? e.describe() : 'Smazání selhalo', kind: 'error' });
      await load();
    }
  }

  // Všichni vidí, co čeká i co už je objednané (ať se neobjednává dvakrát):
  // nahoře čekající, pod nimi objednané za posledních 30 dní; starší na přání.
  const now = Date.now();
  const cas = (p: Pozadavek) => p.updated_at ?? p.created_at;
  const vsechny = items ?? [];
  const cekajici = vsechny.filter((p) => p.stav === 'nova');
  const objednaneVse = vsechny.filter((p) => p.stav !== 'nova').sort((a, b) => cas(b) - cas(a));
  const objednane = hotove
    ? objednaneVse
    : objednaneVse.filter((p) => now - cas(p) < 30 * 24 * 60 * 60 * 1000);
  const visible = [...cekajici, ...objednane];
  const otevrene = cekajici.length;
  const skrytych = objednaneVse.length - objednane.length;

  return (
    <>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text">Nová objednávka</h1>
      </div>

      <div className="bg-surface rounded-xl shadow-card px-6 py-5 mb-6 max-w-3xl">
        <label htmlFor="objednavka-text" className="block text-sm font-semibold text-text mb-1.5">
          Text objednávky
        </label>
        <textarea
          id="objednavka-text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={4}
          maxLength={2000}
          placeholder="Detailně popište svůj požadavek…"
          className="w-full rounded-lg border border-border bg-bg px-3 py-2 text-base text-text focus:outline-none focus:border-primary"
        />
        <label
          htmlFor="objednavka-jmeno"
          className="block text-sm font-semibold text-text mt-4 mb-1.5"
        >
          Jméno a příjmení
        </label>
        <input
          id="objednavka-jmeno"
          value={jmeno}
          onChange={(e) => setJmeno(e.target.value)}
          maxLength={120}
          placeholder="Zadejte své jméno a příjmení"
          className="w-full rounded-lg border border-border bg-bg px-3 py-2 text-base text-text focus:outline-none focus:border-primary"
        />
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={odeslat}
            disabled={busy}
            className="bg-action hover:bg-action-dark disabled:opacity-50 text-white text-base font-bold rounded-lg px-6 py-2.5 cursor-pointer transition-colors"
          >
            {busy ? 'Odesílám…' : 'Odeslat objednávku'}
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between gap-4 mb-3 flex-wrap">
        <h2 className="text-xl font-bold text-text">
          Seznam objednávek{' '}
          <span className="text-base font-semibold text-text-muted">({otevrene} čeká)</span>
        </h2>
        {(skrytych > 0 || hotove) && (
          <label className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer">
            <input type="checkbox" checked={hotove} onChange={(e) => setHotove(e.target.checked)} />
            Zobrazit starší objednané{skrytych > 0 ? ` (${skrytych})` : ''}
          </label>
        )}
      </div>

      <div className="bg-surface rounded-xl shadow-card overflow-x-auto">
        {/* table-fixed + procenta: v úzkém okně se sloupce zúží a text zalomí,
            místo aby tabulka roztáhla stránku do šířky (staré Safari). */}
        <table className="w-full min-w-[36rem] table-fixed text-sm">
          <thead>
            <tr className="bg-bg text-left text-xs font-semibold text-text-secondary">
              <th className="px-4 py-2.5">Text objednávky</th>
              <th className="px-4 py-2.5 w-[18%]">Jméno a příjmení</th>
              <th className="px-4 py-2.5 w-[16%]">Kdy</th>
              <th className="px-4 py-2.5 w-[20%]">Stav</th>
              <th className="px-4 py-2.5 w-10" />
            </tr>
          </thead>
          <tbody>
            {items == null ? (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-text-muted">
                  Načítám…
                </td>
              </tr>
            ) : visible.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-text-muted">
                  Zatím tu nic není.
                </td>
              </tr>
            ) : (
              visible.map((p) => (
                <tr
                  key={p.id}
                  className={`border-b border-border-light align-top ${
                    p.stav !== 'nova' ? 'bg-success-light' : ''
                  }`}
                >
                  <td className="px-4 py-2 font-semibold text-text whitespace-pre-wrap break-words">
                    {/* Objednané zeleně s fajfkou — ať je hned vidět, že se to už řeší. */}
                    {p.stav !== 'nova' && (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                        className="inline-block mr-1.5 -mt-0.5 text-success"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                    {p.text}
                  </td>
                  <td className="px-4 py-2 text-text-secondary break-words">
                    {p.zdroj === 'automat' ? '⚙️ Automaticky (sklad zipů)' : p.jmeno}
                  </td>
                  <td className="px-4 py-2 text-text-secondary whitespace-nowrap">
                    {fmtDateTime(p.created_at)}
                  </td>
                  <td className="px-4 py-2">
                    {canManage ? (
                      <select
                        value={p.stav === 'vyrizeno' ? 'objednano' : p.stav}
                        onChange={(e) => zmenitStav(p, e.target.value as Stav)}
                        title={
                          p.updated_at
                            ? `Naposledy ${fmtDateTime(p.updated_at)}${p.updated_by ? ` — ${p.updated_by}` : ''}`
                            : undefined
                        }
                        className={`w-full max-w-full rounded-md px-2 py-1 text-sm font-semibold cursor-pointer border border-transparent ${STAV_CLASS[p.stav]}`}
                      >
                        {STAVY_VYBER.map((s) => (
                          <option key={s} value={s}>
                            {STAV_LABEL[s]}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span
                        className={`inline-block rounded-md px-2 py-1 text-sm font-semibold ${STAV_CLASS[p.stav]}`}
                      >
                        {STAV_LABEL[p.stav]}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {canManage && (
                      <button
                        type="button"
                        onClick={() => smazat(p)}
                        title="Smazat objednávku"
                        className="text-text-muted hover:text-danger text-lg leading-none cursor-pointer"
                      >
                        ×
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {toast && (
        <StatusModal message={toast.msg} kind={toast.kind} onClose={() => setToast(null)} />
      )}
    </>
  );
}
